import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  Chip,
  Divider,
  IconButton,
  InputAdornment,
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@material-ui/core';
import PerfectScrollbar from 'react-perfect-scrollbar';
import BackspaceOutlinedIcon from '@material-ui/icons/BackspaceOutlined';
import FileCopyIcon from '@material-ui/icons/FileCopy';

import PlusOneIcon from '@material-ui/icons/PlusOne';
import { LinkComponent, LoaderBar } from 'src/components';
import { ProductTypeEnum } from 'src/enums';
import {
  AmountSrpChange,
  ChangeSrpPayload,
  GetTransactionsViaTransactionNoResponse,
  Transaction,
  TransactionDetail
} from 'src/types';
import {
  copyToClipboard,
  formatDate,
  formatCurrency,
  srpPercentageToNumber
} from 'src/utils';
import { useNavigate } from 'react-router-dom';
import { usePermissions, useSnackBar } from 'src/hooks';
import { colors, srpOptions } from 'src/constants';
import { multiBranchFeat } from 'src/constants/feature-toggle';
import { cloneDeep } from 'lodash';
import { sortProductsByCategory } from 'src/utils/categories/sortByCategory';
import { SplitButtons } from 'src/components/buttons/SplitButtons';
import { SrpChangesDialog } from './SrpChangesDialog';
import useResolution from 'src/hooks/useResolution';
import { SRPOption } from 'src/types/srp-options';
import { slices, useAppDispatch, useAppSelector } from 'src/redux';
import {
  applyVoucherThunk,
  deleteVoucherTransaactionThunk,
  // getVoucherThunk,
  validateVoucherThunk
} from 'src/redux/slices/voucher/thunks';
import { unwrapResult } from '@reduxjs/toolkit';
import AddCircleIcon from '@material-ui/icons/AddCircle';
import DeleteIcon from '@material-ui/icons/Delete';
import SyncAltIcon from '@material-ui/icons/SyncAlt';
import { ChangeSerialNoDialog } from '../../components/ChangeSerialNoDialog';
import {
  onTapTapCustomerPaymentAddedViaInternal,
  onTapTapCustomerPaymentDeleteViaInternal
} from 'src/redux/slices/taptap-customer';
import useUserInfo from 'src/hooks/user/use-user-info';

const useStyles = makeStyles((theme) => ({
  cardContainer: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2)
  },
  addBtn: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1)
  },
  extraInfo: {
    margin: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column'
  }
}));

interface Props {
  isLoading?: boolean;
  onAddItemPress: () => void;
  transactionInfo?: GetTransactionsViaTransactionNoResponse;
  transactionDetails?: TransactionDetail;
  onVoidItem: (data: Transaction) => void;
  onReplaceItemPress: (data: Transaction) => void;
  // setTransactionToRma: (data: Transaction) => void;
  getDeleteTransactionInfo: () => void;
  onChangeSrp: (params?: ChangeSrpPayload) => void;
  getTransaction: () => void;
  getTransactionPayments: () => void;
  onHandleFailedChangeSerial?: (failed: boolean) => void;
}

const { selectors: paymentSelectors } = slices.transactionPayments;

const TransactionsCardWithTable = ({
  isLoading,
  // setTransactionToRma,
  transactionInfo,
  transactionDetails,
  onAddItemPress,
  onVoidItem,
  getDeleteTransactionInfo,
  onChangeSrp,
  getTransaction,
  getTransactionPayments,
  onHandleFailedChangeSerial,
  // eslint-disable-next-line no-unused-vars
  onReplaceItemPress
}: Props) => {
  const classes = useStyles();
  const navigate = useNavigate();
  const permissions = usePermissions();
  const { isMobile } = useResolution();
  const dispatch = useAppDispatch();
  const snackbar = useSnackBar();
  const paymentDetails = useAppSelector(
    paymentSelectors.selectTransactionPaymentsResponse
  );
  const { userDetails } = useUserInfo();
  const {
    // canSRPToggleElevenPercent,
    // canSRPToggleNegativeOnePercent,
    canSRPToggleTwelvePercent,
    canSRPToggleZeroPercent
  } = usePermissions();

  const [changeSrp, setChangeSrp] = useState<boolean>(false);
  const [srpValue, setSrpValue] = useState<SRPOption>(srpOptions.promoPrice);
  const [selectedVoucherCode, setSelectedVoucherCode] = useState('');
  const [isOpenChangeSerialNoDialog, setIsOpenChangeSerialNoDialog] = useState<
    boolean
  >(false);
  const [currentSerialNo, setCurrentSerialNo] = useState<string>('');
  const [productName, setProductName] = useState<string>('');
  const [branch, setBranch] = useState<number>(0);

  const isSrpPercentageUnchanged = useMemo(() => {
    const srp = transactionDetails?.srp_percentage;
    const converted = srp === '-100%' ? '-1%' : srp;
    return srpValue?.value === converted;
  }, [srpValue, transactionDetails]);

  const localSrpOptions = useMemo(() => {
    return [
      ...(canSRPToggleZeroPercent ? [srpOptions.promoPrice] : []),
      ...(canSRPToggleTwelvePercent ? [srpOptions.srp] : [])
      // ...(canSRPToggleNegativeOnePercent ? [srpOptions.with2307] : []),
      // ...(canSRPToggleElevenPercent ? [srpOptions.srpWith2307] : [])
    ];
  }, [
    canSRPToggleZeroPercent,
    canSRPToggleTwelvePercent
    // canSRPToggleNegativeOnePercent,
    // canSRPToggleElevenPercent
  ]);

  const intSrpVal = useMemo(() => {
    return srpValue ? parseFloat(srpValue.value.replace('%', '')) / 100 : 1;
  }, [srpValue]);

  const currentSRPRaw = useMemo(() => {
    const raw = transactionDetails?.srp_percentage_raw;
    return raw === '-1.00'
      ? -0.01
      : Number(transactionDetails?.srp_percentage_raw) || 0;
  }, [transactionDetails]);

  const transactionItems = useMemo(() => {
    const validTransactionItems = transactionInfo?.items
      ?.filter(
        (item) =>
          !(
            (item?.isDeleted === 1 && item?.srp_exempted === 1) ||
            (item?.isDeleted === 0 && item?.srp_exempted === 1) ||
            (item?.isDeleted === 1 && item?.srp_exempted === 0) ||
            (item?.retail_price != null && item?.retail_price <= 0)
          )
      )
      .map((item) => ({
        product_id: item?.product_id,
        product_name: item?.product_name,
        retail_price: item?.retail_price
      }));
    return validTransactionItems;
  }, [transactionInfo]);

  const removeSRP = useCallback(
    (price: number) => {
      return price / (1 + currentSRPRaw);
    },
    [currentSRPRaw]
  );

  const applySRP = useCallback(
    (price: number) => {
      return price + price * intSrpVal;
    },
    [intSrpVal]
  );

  const appliedSrpChangesItems = useMemo(() => {
    return transactionItems?.map((item) => {
      // Ensure we only use the fallback if the retail price is undefined or null
      const originalPrice =
        item?.retail_price != null ? removeSRP(item.retail_price) : 999999;
      const newRetailPrice = applySRP(originalPrice);

      return {
        product_name: item?.product_name,
        breakdown: `from ${formatCurrency(originalPrice)} to ${formatCurrency(
          newRetailPrice
        )}`,
        new_retail_price: newRetailPrice
      };
    });
  }, [applySRP, removeSRP, transactionItems]);

  // computed prev and new total amount
  const appliedTransactionTotalAmountChange: AmountSrpChange = useMemo(() => {
    const baseTotalAmount = transactionItems?.reduce((accumulator, current) => {
      if (current?.retail_price) {
        return accumulator + removeSRP(current?.retail_price);
      }
      return accumulator;
    }, 0);

    const newTotalAmount = appliedSrpChangesItems?.reduce(
      (accumulator, current) => {
        if (current?.new_retail_price) {
          return accumulator + current?.new_retail_price;
        }
        return accumulator;
      },
      0
    );
    return {
      prev_amount: baseTotalAmount,
      new_amount: newTotalAmount
    };
  }, [appliedSrpChangesItems, removeSRP, transactionItems]);

  //sorted values
  const sortedTransactionItems = useMemo(() => {
    if (transactionInfo?.items) {
      const sortedTransactionItems = cloneDeep(transactionInfo?.items) || [];
      return sortProductsByCategory(sortedTransactionItems);
    }
    return [];
  }, [transactionInfo]);

  const itemSerialNo = useCallback((item: Transaction) => {
    if (item?.product_type === ProductTypeEnum.Consumable) {
      return '--';
    }
    if (item?.serial_no) {
      return item?.serial_no;
    }
    return '--';
  }, []);

  const onHandleChangeSrp = useCallback(() => {
    if (
      appliedTransactionTotalAmountChange?.prev_amount != null &&
      appliedTransactionTotalAmountChange.prev_amount <= 0
    ) {
      return;
    }
    if (!srpValue) {
      console.error('Invalid srp value');
      return;
    }

    onChangeSrp({
      transaction_no: transactionDetails?.transaction_no,
      srp_percentage: srpPercentageToNumber(srpValue.value)
    });
  }, [
    appliedTransactionTotalAmountChange,
    onChangeSrp,
    srpValue,
    transactionDetails
  ]);

  // const fetchVouchers = async () => {
  //   try {
  //     const response = unwrapResult(
  //       await dispatch(getVoucherThunk(transactionInfo?.total_price || 0))
  //     );

  //     return response?.originalData?.data || [];
  //   } catch (error) {
  //     console.error('Error fetching vouchers:', error);
  //     return [];
  //   }
  // };

  //TODO: check here
  const applyVoucher = async (items: Transaction[]) => {
    // Only pass items here that are not yet voided
    const cleanedItems = items?.filter((item) => !item?.isDeleted);

    try {
      const validateVoucher = unwrapResult(
        await dispatch(
          validateVoucherThunk({
            voucher_code: selectedVoucherCode || '',
            total_amount: transactionInfo?.total_price || 0,
            products: cleanedItems?.map((item) => ({
              product_id: item?.product_id,
              product_name: item?.product_name,
              amount: item?.retail_price,
              category_id: item?.category_id
            }))
          })
        )
      );

      if (!validateVoucher?.success) {
        snackbar.show({
          severity: 'error',
          message:
            validateVoucher?.errors?.message ||
            validateVoucher?.message ||
            'Voucher is not valid'
        });
        return;
      }

      // eslint-disable-next-line no-unreachable
      const response = unwrapResult(
        await dispatch(
          applyVoucherThunk({
            transaction_no: transactionInfo?.transaction_no || '',
            voucher_code: selectedVoucherCode || ''
          })
        )
      );

      if (response?.success) {
        snackbar.show({
          severity: 'success',
          message: 'Voucher applied successfully'
        });

        onTapTapCustomerPaymentAddedViaInternal({
          transaction_no: transactionInfo?.transaction_no,
          category: 'payment',
          payment_type: 'VOUCHER',
          amount: validateVoucher?.originalData?.data?.total_discounted_price,
          acknowledgement_receipt_no: ''
        });

        setTimeout(() => {
          window.location.reload();
        }, 1000);
      } else {
        snackbar.show({
          severity: 'error',
          message: 'Failed to apply voucher'
        });
      }
    } catch (error) {
      console.error('Error apply voucher:', error);
      snackbar.show({
        severity: 'error',
        message: 'An error occurred while apply the voucher'
      });
    }
  };

  const deleteVoucher = async () => {
    const response = unwrapResult(
      await dispatch(
        deleteVoucherTransaactionThunk({
          transaction_no: transactionInfo?.transaction_no || '',
          voucher_code: transactionDetails?.voucher?.code || ''
        })
      )
    );

    const dataPaymentToBeDeleted = paymentDetails?.data?.find(
      (x) => x.payment_type === 'VOUCHER'
    );

    // sync the delete the voucher from taptap customer payment
    await onTapTapCustomerPaymentDeleteViaInternal({
      paymentToBeDeleted: dataPaymentToBeDeleted,
      transactionNo: transactionInfo?.transaction_no || '',
      userDetailsId: userDetails?.id
    });

    if (response?.success) {
      snackbar.show({
        severity: 'success',
        message: 'Voucher deleted successfully'
      });
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } else {
      const getErrorMessage = (errors: any) => {
        if (!errors) return 'An unknown error occurred.';

        const transactionNoErrors = errors?.transaction_no?.join('\n');
        const voucherCodeErrors = errors?.voucher_code?.join('\n');

        return (
          [transactionNoErrors, voucherCodeErrors].filter(Boolean).join('\n') ||
          'An error occurred.'
        );
      };

      snackbar.show({
        severity: 'error',
        message: getErrorMessage(response?.errors)
      });
    }
  };

  const deletedChip = (item: Transaction) => {
    return (
      <Tooltip
        title={
          <>
            <div>Void reason: {item?.void_reason ?? '-'}</div>
            <div>
              Void date:{' '}
              {item?.updated_at
                ? formatDate(item.updated_at, 'MMM d, yyyy h:mm a')
                : '-'}
            </div>
          </>
        }
      >
        <Chip
          label="Deleted"
          style={{ height: '100%', marginLeft: 10 }}
          size="small"
          variant="outlined"
          color="primary"
        />
      </Tooltip>
    );
  };

  useEffect(() => {
    const srp = transactionDetails?.srp_percentage;
    if (srp === '-100%') {
      setSrpValue(srpOptions.with2307);
      return;
    }
    const option = Object.entries(srpOptions).find(
      (option) => option[1].value === srp
    );
    if (!option) {
      console.error('cannot find option');
      return;
    }
    setSrpValue(srpOptions[option[0]]);
  }, [transactionDetails]);

  return (
    <div>
      <LoaderBar isLoading={isLoading || false} />
      <Card className={classes.cardContainer}>
        <PerfectScrollbar>
          <Box minWidth={1050}>
            <Table>
              <TableHead>
                <TableRow>
                  {multiBranchFeat && <TableCell>Branch name</TableCell>}
                  <TableCell>Category</TableCell>
                  <TableCell>Product Name</TableCell>
                  <TableCell>Serial No.</TableCell>
                  <TableCell>Unit Price</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell>Voided By</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedTransactionItems?.map((item: Transaction) => (
                  <React.Fragment key={item.id}>
                    <TableRow
                      hover
                      style={
                        item?.isDeleted ? { backgroundColor: colors.error } : {}
                      }
                    >
                      {multiBranchFeat && (
                        <TableCell>
                          <LinkComponent title={item?.branch_name ?? '--'} />
                        </TableCell>
                      )}
                      <TableCell>
                        <LinkComponent title={item?.category_name ?? '--'} />
                      </TableCell>
                      <TableCell>
                        <LinkComponent
                          onClick={() =>
                            navigate(`/app/products/${item?.product_id}`)
                          }
                          href={`/app/products/${item?.product_id}`}
                          title={item?.product_name ?? '--'}
                        />
                        {item.is_pc_bundle ? (
                          <Chip
                            label="PC Bundle"
                            style={{ height: '100%', marginLeft: 10 }}
                            size="small"
                            variant="outlined"
                          />
                        ) : null}
                        {item.is_product_bundle ? (
                          <Chip
                            label="Bundle"
                            style={{ height: '100%', marginLeft: 10 }}
                            size="small"
                            variant="outlined"
                          />
                        ) : null}
                        {item.isDeleted ? deletedChip(item) : null}
                      </TableCell>
                      <TableCell>
                        {item.isDeleted ? (
                          itemSerialNo(item)
                        ) : (
                          <Box
                            display="flex"
                            alignItems="center"
                            style={{ gap: 5 }}
                          >
                            <LinkComponent
                              onClick={() =>
                                navigate(
                                  `/app/products-listings/${item.listing_id}`
                                )
                              }
                              href={`/app/products-listings/${item.listing_id}`}
                              title={itemSerialNo(item)}
                            />
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                copyToClipboard(itemSerialNo(item));
                              }}
                            >
                              <FileCopyIcon fontSize="small" />
                            </IconButton>
                            {/* TODO: For now we only allow change serial on RAM */}
                            {item?.category_name === 'RAM' ? (
                              <Tooltip title={"Change item's serial number"}>
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    setCurrentSerialNo(item?.serial_no || '');
                                    setProductName(item?.product_name || '');
                                    setBranch(item?.branch_id || 0);
                                    setIsOpenChangeSerialNoDialog(true);
                                  }}
                                >
                                  <SyncAltIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            ) : null}
                          </Box>
                        )}
                      </TableCell>
                      <TableCell>
                        {formatCurrency(item?.retail_price)}
                      </TableCell>
                      <TableCell>
                        {formatCurrency(item?.amount || item?.retail_price)}
                      </TableCell>
                      <TableCell>
                        {item?.deleted_by_name && (
                          <Tooltip title={item?.deleted_by_name}>
                            <Chip
                              label={item?.deleted_by_name}
                              variant="outlined"
                              style={{ overflow: 'hidden', maxWidth: '120px' }}
                            />
                          </Tooltip>
                        )}
                      </TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <Tooltip title={`Void ${item?.product_name}`}>
                          <IconButton
                            disabled={!!item?.isDeleted}
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              onVoidItem(item);
                              getDeleteTransactionInfo();
                            }}
                          >
                            <BackspaceOutlinedIcon color="secondary" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>

                    {item.bundle_items?.map((bundleItem) => (
                      <TableRow
                        key={bundleItem.listing_id}
                        hover
                        style={{
                          backgroundColor: item?.isDeleted
                            ? colors.error
                            : '#f9f9f9'
                        }}
                      >
                        {multiBranchFeat && <TableCell>--</TableCell>}
                        <TableCell>
                          <LinkComponent title={bundleItem.category_name || ''} />
                        </TableCell>
                        <TableCell>
                          <LinkComponent
                            onClick={() =>
                              navigate(`/app/products/${bundleItem.product_id}`)
                            }
                            href={`/app/products/${bundleItem.product_id}`}
                            title={bundleItem.product_name || ''}
                          />
                          {item.is_product_bundle ? (
                            <Chip
                              label={`Bundle Item`}
                              style={{ height: '100%', marginLeft: 10 }}
                              size="small"
                              variant="outlined"
                            />
                          ) : null}
                          {item.isDeleted ? deletedChip(item) : null}
                        </TableCell>
                        <TableCell>
                          <LinkComponent
                            onClick={() =>
                              navigate(
                                `/app/products-listings/${bundleItem.listing_id}`
                              )
                            }
                            href={`/app/products-listings/${bundleItem.listing_id}`}
                            title={bundleItem.serial_no || ''}
                          />
                        </TableCell>
                        <TableCell>
                          {formatCurrency(bundleItem.retail_price)}
                        </TableCell>
                        <TableCell>
                          {formatCurrency(bundleItem.retail_price)}
                        </TableCell>
                        <TableCell>--</TableCell>
                        <TableCell></TableCell>
                      </TableRow>
                    ))}
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>

            <div
              style={{
                display: 'flex',
                justifyContent: isMobile ? 'start' : 'end',
                paddingLeft: isMobile ? '1em' : 'initial'
              }}
            >
              <div
                className={classes.extraInfo}
                style={{ alignItems: isMobile ? 'start' : 'end' }}
              >
                <Button
                  disabled={isLoading}
                  startIcon={<PlusOneIcon />}
                  onClick={() => onAddItemPress()}
                  variant="outlined"
                  color="primary"
                  className={classes.addBtn}
                >
                  Add item in transaction
                </Button>

                <div style={{ marginBottom: 15 }}>
                  {!isLoading ? (
                    <SplitButtons
                      initial_val={srpValue?.name ?? ''}
                      applyPermissions={true}
                      disableButton={
                        isSrpPercentageUnchanged ||
                        (appliedTransactionTotalAmountChange?.prev_amount !=
                          null &&
                          appliedTransactionTotalAmountChange.prev_amount <= 0)
                      }
                      title="Current Pricing"
                      options={localSrpOptions}
                      onChange={(option: SRPOption) => {
                        setSrpValue(option);
                      }}
                      onApplyClick={() => {
                        setChangeSrp(true);
                      }}
                    />
                  ) : null}
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
                  <TextField
                    placeholder="Enter Voucher Code"
                    variant="outlined"
                    fullWidth
                    label={
                      transactionDetails?.voucher?.code
                        ? 'Voucher Code'
                        : 'Enter Voucher Code'
                    }
                    size="small"
                    value={
                      transactionDetails?.voucher?.code || selectedVoucherCode
                    }
                    onChange={(event) =>
                      setSelectedVoucherCode(event.target.value)
                    }
                    InputProps={{
                      readOnly: Boolean(transactionDetails?.voucher?.code),
                      endAdornment: (
                        <InputAdornment position="end">
                          <Box
                            sx={{
                              height: 24,
                              mx: 1,
                              display: 'flex',
                              alignItems: 'center'
                            }}
                          >
                            <Divider orientation="vertical" flexItem />
                          </Box>
                          <IconButton
                            onClick={() => {
                              transactionDetails?.voucher?.code
                                ? deleteVoucher()
                                : applyVoucher(transactionInfo?.items || []);
                            }}
                            style={{
                              color: transactionDetails?.voucher?.code
                                ? 'red'
                                : 'green',
                              padding: '5px'
                            }}
                          >
                            {transactionDetails?.voucher?.code ? (
                              <DeleteIcon />
                            ) : (
                              <AddCircleIcon />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                      style: {
                        height: '40px', // Match height of the SRP selector
                        borderRadius: '8px',
                        paddingRight: '5px',
                        color: transactionDetails?.voucher?.code
                          ? 'gray'
                          : 'black'
                      }
                    }}
                  />
                </div>
                <SrpChangesDialog
                  srpValue={srpValue?.name}
                  isOpen={changeSrp}
                  appliedSrpChangesItem={appliedSrpChangesItems}
                  amountSrpChange={appliedTransactionTotalAmountChange}
                  title={`Change Transaction SRP Percentage By ${srpValue?.name}`}
                  subTitle={
                    'Confirm Breakdown of the following SRP changes for each items in transaction.'
                  }
                  onHandleConfirmAction={() => {
                    onHandleChangeSrp();
                  }}
                  onHandleClose={() => setChangeSrp(false)}
                />
                {permissions?.canViewSales ? (
                  <Typography color="textSecondary" variant="subtitle2">
                    sub total:{' '}
                    {`${formatCurrency(transactionInfo?.sub_total_price) ??
                      `0.00`}`}
                  </Typography>
                ) : null}
                {permissions?.canViewSales ? (
                  <Typography color="textSecondary" variant="subtitle2">
                    deducted kachi points:
                    {`-${formatCurrency(
                      Math.abs(Number(transactionInfo?.kachi_points) || 0)
                    )}`}
                  </Typography>
                ) : null}

                <Typography color="textSecondary" variant="subtitle2">
                  Voucher Discount:
                  {`-${formatCurrency(
                    Math.abs(Number(transactionInfo?.voucher_discount || 0))
                  )}`}
                </Typography>

                <Typography variant="h4">
                  TOTAL PRICE:{' '}
                  {`${formatCurrency(transactionInfo?.total_price) ?? `0.00`}`}
                </Typography>
                {permissions?.canViewSales ? (
                  <Typography color="textSecondary" variant="subtitle2">
                    total dealers price:{' '}
                    {`${formatCurrency(transactionInfo?.total_dealers_price) ??
                      `0.00`}`}
                  </Typography>
                ) : null}
                {permissions?.canViewSales ? (
                  <Typography color="textSecondary" variant="subtitle2">
                    total gross:{' '}
                    {`${formatCurrency(transactionInfo?.total_gross) ??
                      `0.00`}`}
                  </Typography>
                ) : null}
              </div>
            </div>
          </Box>
        </PerfectScrollbar>

        <ChangeSerialNoDialog
          isOpen={isOpenChangeSerialNoDialog}
          onHandleClose={() => setIsOpenChangeSerialNoDialog(false)}
          transactionId={transactionDetails?.transaction_no}
          currentSerialNo={currentSerialNo}
          productName={productName}
          branch={branch}
          ecOrderId={transactionDetails?.ec_order_id}
          getTransaction={getTransaction}
          getTransactionPayments={getTransactionPayments}
          onFailedChange={onHandleFailedChangeSerial}
        />
      </Card>
    </div>
  );
};

export const TransactionsCard = React.memo(TransactionsCardWithTable);
